<script setup lang="ts">
import debounce from 'lodash/debounce'

const restaurantStore = useRestaurantStore()
const { t } = useLocale()
const contentStore = useContentStore()
const layoutStore = useLayoutStore()
const isMobile = inject<Ref<boolean>>('isMobile')
const { canShowLupapSearch } = useLupap()

const stickyHeader = ref(false)
const mobileSearch = ref(false)

const imageUrl = computed(() => {
  if (isMobile?.value) {
    return contentStore.site?.header?.mobileHeroImage?.url || `/images/mobileHero.png`
  } else {
    return contentStore.site?.header?.heroImage?.url || `/images/hero.png`
  }
})

const landingHeaderStyle = computed(() => {
  if (stickyHeader.value) return 'background: white;'
  return imageUrl.value
    ? `background: url(${imageUrl.value}); background-repeat: no-repeat; background-size: cover`
    : ''
})

const onScroll = () => {
  const landingContentElem = document.querySelector(
    '[data-test="mcd-landing-header"]'
  ) as HTMLElement
  if (!landingContentElem) return
  const isZeroScroll = window.scrollY <= 0
  stickyHeader.value = isZeroScroll ? false : window.scrollY > landingContentElem.scrollHeight
  onScrollDebounced.cancel()
}

const onScrollDebounced = debounce(onScroll, 250)

const onMobileSearchChangeVisibility = () => {
  mobileSearch.value = !mobileSearch.value
}

onMounted(() => {
  if (process.client) {
    window.addEventListener('scroll', onScrollDebounced)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScrollDebounced, false)
})
</script>

<template>
  <div class="left-0 top-0 z-10" :class="{ sticky: stickyHeader }">
    <!-- Smartbanner -->
    <ClientOnly>
      <LazySmartbanner v-if="layoutStore.smartbanner" :config="layoutStore.smartbanner" />
    </ClientOnly>

    <div
      data-test="mcd-landing-header"
      class="ease relative z-10 mx-auto h-[320px] bg-cover bg-no-repeat transition-all duration-[850ms] md:h-[552px]"
      :style="landingHeaderStyle"
      :class="{
        'h-[4rem] shadow-down-xs md:h-[8rem]': stickyHeader,
      }"
    >
      <div
        class="absolute flex h-full w-full"
        :class="{
          'bg-[#292929b3] md:bg-inherit md:bg-header-gradient-md': !stickyHeader,
        }"
      >
        <div class="absolute h-full w-full">
          <Header :is-landing="true" :sticky-landing="stickyHeader" />
        </div>
        <div
          v-if="!stickyHeader"
          class="absolute left-0 right-0 top-[100px] mx-auto text-center md:left-28 md:right-auto md:top-60 md:text-left"
        >
          <div>
            <p class="text-2xl font-bold text-white md:text-5xl">{{ t('home.searchTitle') }}</p>
          </div>
          <ToggleSearch
            :candidate-area="restaurantStore.candidateArea"
            :area-selected="restaurantStore.areaSelected"
            @set-area="restaurantStore.setArea"
          />

          <LazySearchBar
            v-if="restaurantStore.candidateArea"
            absolute-places
            :open-search="isMobile || canShowLupapSearch"
            :search-screen="canShowLupapSearch"
            @search-mobile="onMobileSearchChangeVisibility"
          />
        </div>
      </div>
    </div>
  </div>
  <template v-if="mobileSearch">
    <LazySearchLupap v-if="canShowLupapSearch" @close="onMobileSearchChangeVisibility" />
    <LazySearchMobileModal v-else @close="onMobileSearchChangeVisibility" />
  </template>
</template>
